import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, BookOpen, Calculator, FlaskConical, Trophy,
  Users, Star, CheckCircle2, Sparkles, Rocket,
  Gamepad2, Brain, Pencil, Headphones, Heart, Zap, Smile
} from 'lucide-react';

// Inline keyframe animations for child-friendly bouncy/wiggle motion
const FunStyles = () => (
  <style>{`
    @keyframes wiggle { 0%,100% { transform: rotate(-3deg); } 50% { transform: rotate(3deg); } }
    @keyframes float-y { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
    @keyframes pop-in { 0% { transform: scale(0.5); opacity:0; } 60% { transform: scale(1.1); } 100% { transform: scale(1); opacity:1; } }
    @keyframes spin-slow { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
    @keyframes rainbow-shift { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
    .anim-wiggle { animation: wiggle 2.5s ease-in-out infinite; transform-origin: center; }
    .anim-float { animation: float-y 4s ease-in-out infinite; }
    .anim-float-slow { animation: float-y 6s ease-in-out infinite; }
    .anim-pop { animation: pop-in 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
    .anim-spin-slow { animation: spin-slow 18s linear infinite; }
    .font-fun { font-family: 'Fredoka', 'Nunito', sans-serif; letter-spacing: -0.01em; }
    .rainbow-text {
      background: linear-gradient(90deg, #f472b6, #fb923c, #facc15, #34d399, #60a5fa, #a78bfa);
      background-size: 200% 200%;
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      animation: rainbow-shift 6s ease-in-out infinite;
    }
    .sticker-shadow { box-shadow: 0 8px 0 rgba(0,0,0,0.08), 0 12px 30px rgba(0,0,0,0.08); }
    .squiggle-underline {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 120 8'%3E%3Cpath d='M0 4 Q15 0 30 4 T60 4 T90 4 T120 4' stroke='%23fbbf24' stroke-width='4' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: bottom left;
      background-size: 100% 8px;
      padding-bottom: 10px;
    }
  `}</style>
);

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-white overflow-hidden font-fun" data-testid="landing-page">
      <FunStyles />

      {/* Header */}
      <header className="bg-white/95 backdrop-blur-md border-b-2 border-amber-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center text-white font-extrabold text-lg shadow-md anim-wiggle">
              GM
            </div>
            <span className="font-extrabold text-xl text-slate-800">Growing Minds</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login">
              <Button variant="ghost" className="font-bold text-slate-600 hover:text-emerald-600 rounded-full" data-testid="login-btn">Log In</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 font-extrabold rounded-full px-5 sm:px-6 shadow-md hover:shadow-lg transition-all" data-testid="register-btn">
                Join the Fun!
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero - Super Playful */}
      <section className="relative py-12 lg:py-20" style={{background: 'linear-gradient(135deg, #FEF3C7 0%, #FCE7F3 35%, #DBEAFE 70%, #D1FAE5 100%)'}}>
        {/* Floating sticker decorations */}
        <div className="absolute top-8 left-6 sm:left-16 anim-float-slow text-5xl select-none" aria-hidden="true">
          <Star className="w-12 h-12 text-amber-400 fill-amber-400 drop-shadow-md" />
        </div>
        <div className="absolute top-24 right-10 anim-float text-4xl select-none" aria-hidden="true">
          <Heart className="w-10 h-10 text-pink-400 fill-pink-400 drop-shadow-md" />
        </div>
        <div className="absolute bottom-16 left-1/4 anim-float select-none" aria-hidden="true" style={{animationDelay: '1.5s'}}>
          <Sparkles className="w-9 h-9 text-violet-400 fill-violet-300 drop-shadow-md" />
        </div>
        <div className="absolute bottom-32 right-1/3 anim-float-slow select-none" aria-hidden="true">
          <Zap className="w-8 h-8 text-yellow-400 fill-yellow-300 drop-shadow-md" />
        </div>
        <div className="hidden lg:block absolute top-1/2 left-2 anim-spin-slow" aria-hidden="true">
          <div className="w-20 h-20 rounded-full border-4 border-dashed border-amber-300/50" />
        </div>

        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            <div className="anim-pop">
              <div className="inline-flex items-center gap-2 bg-white text-pink-600 px-5 py-2.5 rounded-full text-sm font-extrabold mb-6 shadow-md border-2 border-pink-200">
                <Sparkles className="w-4 h-4 fill-pink-500" /> Where Curious Minds Bloom
              </div>
              <h1 className="font-fun text-5xl sm:text-6xl lg:text-7xl font-bold text-slate-900 leading-[1.05] mb-6">
                Learning <br />
                <span className="rainbow-text">that feels</span><br />
                <span className="inline-block squiggle-underline">like play!</span>
                <span className="inline-block ml-2 anim-wiggle">🚀</span>
              </h1>
              <p className="text-lg sm:text-xl text-slate-700 mb-8 leading-relaxed max-w-xl font-medium">
                Bright lessons, interactive games and big high-fives — your child explores
                English, Maths and Science their way, from KS1 right through to GCSEs.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-lg py-7 px-8 font-extrabold rounded-full shadow-xl shadow-emerald-300/60 hover:shadow-2xl transition-all hover:-translate-y-0.5" data-testid="hero-cta">
                    Start Learning <Rocket className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" className="text-lg py-7 px-8 font-extrabold rounded-full border-2 border-slate-300 bg-white hover:bg-slate-50 text-slate-700">
                    Parent Login
                  </Button>
                </Link>
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-8 text-sm font-bold text-slate-700">
                <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-500" /> SATs &amp; GCSE Prep</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-500" /> 11+ Exam Ready</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-500" /> ESOL Support</div>
              </div>
            </div>

            {/* Hero Visual: collage of fun stickers + image */}
            <div className="relative anim-pop" style={{animationDelay: '0.2s'}}>
              {/* Illustrated cluster (no photo) */}
              <div className="relative rounded-[2rem] border-[6px] border-white sticker-shadow rotate-1 h-[420px] overflow-hidden" style={{background: 'linear-gradient(135deg, #FCE7F3 0%, #DBEAFE 50%, #D1FAE5 100%)'}}>
                {/* Decorative grid of subject icons */}
                <div className="absolute inset-0 p-8 grid grid-cols-3 gap-5 place-items-center">
                  <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center sticker-shadow -rotate-6 anim-float">
                    <Calculator className="w-10 h-10 text-amber-500" />
                  </div>
                  <div className="w-24 h-24 bg-gradient-to-br from-pink-400 to-fuchsia-500 rounded-3xl flex items-center justify-center sticker-shadow rotate-3 anim-wiggle">
                    <BookOpen className="w-12 h-12 text-white" />
                  </div>
                  <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center sticker-shadow rotate-6 anim-float-slow">
                    <FlaskConical className="w-10 h-10 text-emerald-500" />
                  </div>
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-2xl flex items-center justify-center sticker-shadow rotate-3 anim-float-slow">
                    <Pencil className="w-9 h-9 text-white" />
                  </div>
                  <div className="w-24 h-24 bg-white rounded-3xl flex items-center justify-center sticker-shadow -rotate-3 anim-float">
                    <Brain className="w-12 h-12 text-violet-500" />
                  </div>
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-red-500 rounded-2xl flex items-center justify-center sticker-shadow -rotate-6 anim-wiggle">
                    <Headphones className="w-10 h-10 text-white" />
                  </div>
                  <div className="w-20 h-20 bg-gradient-to-br from-yellow-300 to-amber-400 rounded-2xl flex items-center justify-center sticker-shadow rotate-6 anim-float">
                    <Star className="w-10 h-10 text-white fill-white" />
                  </div>
                  <div className="w-24 h-24 bg-white rounded-3xl flex items-center justify-center sticker-shadow rotate-3 anim-float-slow">
                    <Gamepad2 className="w-12 h-12 text-pink-500" />
                  </div>
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center sticker-shadow -rotate-3 anim-wiggle">
                    <Trophy className="w-10 h-10 text-white" />
                  </div>
                </div>
                {/* Soft sparkle overlay */}
                <div className="absolute top-4 right-6"><Sparkles className="w-6 h-6 text-amber-400 fill-amber-300" /></div>
                <div className="absolute bottom-6 left-6"><Sparkles className="w-5 h-5 text-pink-400 fill-pink-300" /></div>
              </div>

              {/* Speech bubble */}
              <div className="absolute -top-4 left-6 bg-yellow-300 px-4 py-3 rounded-2xl rounded-bl-none border-4 border-white sticker-shadow font-extrabold text-slate-800 anim-float">
                <span className="text-base">I just got 5 stars! ⭐</span>
                <div className="absolute -bottom-3 left-4 w-4 h-4 bg-yellow-300 rotate-45" />
              </div>

              {/* Trophy sticker */}
              <div className="absolute -bottom-5 -left-4 bg-white rounded-2xl px-4 py-3 sticker-shadow border-4 border-amber-200 -rotate-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-300 to-orange-400 rounded-xl flex items-center justify-center anim-wiggle">
                    <Trophy className="w-6 h-6 text-white fill-white/30" />
                  </div>
                  <div>
                    <p className="font-extrabold text-slate-800 leading-tight">Earn Stars</p>
                    <p className="text-xs text-slate-500 font-semibold">&amp; cool badges</p>
                  </div>
                </div>
              </div>

              {/* Games sticker */}
              <div className="absolute -top-6 -right-3 bg-white rounded-2xl px-4 py-3 sticker-shadow border-4 border-pink-200 rotate-6 anim-float-slow">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-fuchsia-500 rounded-xl flex items-center justify-center">
                    <Gamepad2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-extrabold text-slate-800 leading-tight">20+ Games</p>
                    <p className="text-xs text-slate-500 font-semibold">play &amp; learn</p>
                  </div>
                </div>
              </div>

              {/* Floating mini-characters */}
              <div className="absolute -bottom-8 right-2 bg-violet-100 border-4 border-white rounded-full w-20 h-20 flex items-center justify-center sticker-shadow anim-float">
                <Smile className="w-10 h-10 text-violet-500" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Friendly stat strip */}
      <section className="bg-white border-y-2 border-dashed border-amber-200 py-6">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {[
            { num: '20+', label: 'Fun Games', color: 'text-pink-500' },
            { num: '300+', label: 'Lessons', color: 'text-emerald-500' },
            { num: '5–16', label: 'Ages Covered', color: 'text-blue-500' },
            { num: '⭐⭐⭐⭐⭐', label: 'Loved by Parents', color: 'text-amber-500' },
          ].map((s) => (
            <div key={s.label} className="px-2">
              <p className={`font-fun font-bold text-3xl sm:text-4xl ${s.color}`}>{s.num}</p>
              <p className="text-sm font-bold text-slate-600 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why Children Love It - Sticker cards */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute top-10 right-10 w-40 h-40 bg-pink-100 rounded-full blur-3xl opacity-60" />
        <div className="absolute bottom-10 left-10 w-48 h-48 bg-blue-100 rounded-full blur-3xl opacity-60" />
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="text-center mb-14">
            <span className="inline-block bg-amber-100 text-amber-700 font-extrabold text-xs uppercase tracking-wide px-4 py-1.5 rounded-full mb-4 border-2 border-amber-200">Why kids love us</span>
            <h2 className="font-fun text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              Lessons that <span className="text-emerald-600">fit your child</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Gamepad2, title: 'Fun Learning Games', desc: 'Grammar Guardians, Space Maths, Fraction Frenzy and more — learning sneakily disguised as play!', bg: 'from-pink-100 to-pink-50', iconBg: 'bg-pink-500', border: 'border-pink-300', rotate: '-rotate-1' },
              { icon: Brain, title: 'Adaptive Learning', desc: 'Lessons grow with your child — getting bouncier when easy, kinder when tricky.', bg: 'from-violet-100 to-violet-50', iconBg: 'bg-violet-500', border: 'border-violet-300', rotate: 'rotate-1' },
              { icon: BookOpen, title: 'AI Reading Adventures', desc: 'Choose-your-own stories where every page is a chance to be the hero.', bg: 'from-blue-100 to-blue-50', iconBg: 'bg-blue-500', border: 'border-blue-300', rotate: '-rotate-1' },
              { icon: Pencil, title: 'Essay Marking', desc: 'Submit a piece of writing and get instant, kind feedback that helps it shine.', bg: 'from-amber-100 to-amber-50', iconBg: 'bg-amber-500', border: 'border-amber-300', rotate: 'rotate-1' },
              { icon: Headphones, title: 'Audio Lessons', desc: 'Friendly narration so your child can listen, learn and follow along anywhere.', bg: 'from-emerald-100 to-emerald-50', iconBg: 'bg-emerald-500', border: 'border-emerald-300', rotate: '-rotate-1' },
              { icon: Trophy, title: 'Stars & Badges', desc: 'Big, bright rewards for every milestone — because progress deserves a party!', bg: 'from-orange-100 to-orange-50', iconBg: 'bg-orange-500', border: 'border-orange-300', rotate: 'rotate-1' },
            ].map((feature) => (
              <div key={feature.title} className={`bg-gradient-to-br ${feature.bg} border-2 ${feature.border} rounded-3xl p-7 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${feature.rotate} hover:rotate-0`}>
                <div className={`w-16 h-16 ${feature.iconBg} rounded-2xl flex items-center justify-center mb-5 sticker-shadow`}>
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-fun text-xl font-bold text-slate-800 mb-2">{feature.title}</h3>
                <p className="text-slate-700 text-sm leading-relaxed font-medium">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subjects - Big colourful sticker cards */}
      <section className="py-20" style={{background: 'linear-gradient(180deg, #FEFCE8 0%, #FFFFFF 60%, #F0FDF4 100%)'}}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-14">
            <span className="inline-block bg-emerald-100 text-emerald-700 font-extrabold text-xs uppercase tracking-wide px-4 py-1.5 rounded-full mb-4 border-2 border-emerald-200">Three Big Adventures</span>
            <h2 className="font-fun text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              Pick a subject, <span className="text-emerald-600">start exploring!</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto font-medium">
              Aligned to the UK National Curriculum from Year 1 to Year 11.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Calculator, name: 'Maths', emoji: '🧮', tagline: 'Numbers feel fun', desc: 'Times tables races, fraction games, mental maths and brain-stretching algebra.', tags: ['Times Tables', 'Fractions', 'Algebra', 'Geometry'], color: 'amber', from: 'from-amber-400', to: 'to-orange-500', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300' },
              { icon: BookOpen, name: 'English', emoji: '📚', tagline: 'Stories come alive', desc: 'Reading adventures, grammar quests, spelling bees and creative writing.', tags: ['Reading', 'Grammar', 'Spelling', 'Writing'], color: 'pink', from: 'from-pink-400', to: 'to-fuchsia-500', bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-300' },
              { icon: FlaskConical, name: 'Science', emoji: '🔬', tagline: 'Curious minds win', desc: 'Biology, Chemistry and Physics with virtual experiments and wow-moments.', tags: ['Biology', 'Chemistry', 'Physics', 'Lab Work'], color: 'emerald', from: 'from-emerald-400', to: 'to-teal-500', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-300' },
            ].map((s, i) => (
              <div key={s.name} className={`bg-white rounded-[2rem] p-8 sticker-shadow border-2 ${s.border} hover:-translate-y-2 transition-all duration-300 ${i % 2 === 0 ? '-rotate-1' : 'rotate-1'} hover:rotate-0`}>
                <div className={`w-20 h-20 bg-gradient-to-br ${s.from} ${s.to} rounded-3xl flex items-center justify-center mb-5 sticker-shadow`}>
                  <s.icon className="w-10 h-10 text-white" />
                </div>
                <p className={`text-xs font-extrabold uppercase tracking-wider ${s.text} mb-1`}>{s.tagline}</p>
                <h3 className="font-fun text-3xl font-bold text-slate-800 mb-3 flex items-center gap-2">{s.name} <span className="text-2xl">{s.emoji}</span></h3>
                <p className="text-slate-600 mb-5 font-medium">{s.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {s.tags.map(t => (
                    <span key={t} className={`${s.bg} ${s.text} text-xs font-extrabold px-3 py-1.5 rounded-full border ${s.border}`}>{t}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Stages - Playful Bubbles */}
      <section className="py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="font-fun text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              Every age. <span className="text-pink-500">Every stage.</span>
            </h2>
            <p className="text-lg text-slate-600 font-medium">From little learners to GCSE champions — we&apos;ve got it all.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-5">
            {[
              { stage: 'KS1', years: 'Years 1-2', ages: 'Ages 5-7', from: 'from-green-400', to: 'to-emerald-500', text: 'text-emerald-700' },
              { stage: 'KS2', years: 'Years 3-6', ages: 'Ages 7-11', from: 'from-blue-400', to: 'to-cyan-500', text: 'text-blue-700' },
              { stage: 'KS3', years: 'Years 7-9', ages: 'Ages 11-14', from: 'from-violet-400', to: 'to-purple-500', text: 'text-violet-700' },
              { stage: 'KS4', years: 'Years 10-11', ages: 'Ages 14-16', from: 'from-orange-400', to: 'to-red-500', text: 'text-orange-700' },
              { stage: 'ESOL', years: 'All Ages', ages: 'English Learners', from: 'from-pink-400', to: 'to-rose-500', text: 'text-pink-700' },
              { stage: '11+', years: 'Years 5-6', ages: 'Grammar School', from: 'from-amber-400', to: 'to-yellow-500', text: 'text-amber-700' },
            ].map((ks, i) => (
              <div key={ks.stage} className={`relative bg-white rounded-3xl p-5 text-center sticker-shadow border-2 border-slate-100 hover:-translate-y-1 transition-all ${i % 2 === 0 ? '-rotate-2' : 'rotate-2'} hover:rotate-0`}>
                <div className={`mx-auto w-14 h-14 mb-3 rounded-2xl bg-gradient-to-br ${ks.from} ${ks.to} flex items-center justify-center sticker-shadow`}>
                  <span className="text-white font-fun font-bold text-lg">{ks.stage}</span>
                </div>
                <p className={`text-base font-extrabold ${ks.text}`}>{ks.years}</p>
                <p className="text-xs text-slate-500 mt-1 font-semibold">{ks.ages}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Parents Section */}
      <section className="py-20 relative" style={{background: 'linear-gradient(135deg, #EFF6FF 0%, #FEF3C7 50%, #F0FDF4 100%)'}}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="anim-pop">
            <div className="inline-flex items-center gap-2 bg-white text-blue-700 px-4 py-2 rounded-full text-sm font-extrabold mb-6 sticker-shadow border-2 border-blue-200">
              <Users className="w-4 h-4" /> For Parents
            </div>
            <h2 className="font-fun text-4xl sm:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              Stay close to <span className="text-blue-600">every win</span>.
            </h2>
            <p className="text-lg text-slate-700 mb-10 font-medium max-w-2xl mx-auto">
              Your own parent dashboard shows progress, strengths and gentle nudges
              for what to practise next — no more guessing, no more nagging.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 max-w-3xl mx-auto text-left">
              {[
                'See progress across every subject and key stage',
                'Spot strengths and areas to give a little boost',
                'Track how often your child is practising',
                'Receive friendly assessment reports',
                'Cheer them on with confidence at home',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/70 backdrop-blur rounded-2xl px-4 py-3 border border-white">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-800 font-semibold">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Exam Boards */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="font-fun text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
              Practice that <span className="text-amber-500">gets results</span>
            </h2>
            <p className="text-base text-slate-600 font-medium">SATs, GCSEs and 11+ prep from all the major exam boards.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
            {['AQA', 'Edexcel', 'OCR', 'Eduqas', 'WJEC', 'SATs', '11+'].map((board, i) => (
              <div key={board} className={`bg-gradient-to-br from-slate-50 to-white border-2 border-slate-200 rounded-2xl px-7 py-4 text-center font-fun font-bold text-slate-700 sticker-shadow hover:-translate-y-1 transition-all ${i % 2 === 0 ? '-rotate-2' : 'rotate-2'} hover:rotate-0`}>
                {board}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden" style={{background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #0891b2 100%)'}}>
        <div className="absolute inset-0 opacity-20" aria-hidden="true">
          <div className="absolute top-10 left-10 w-40 h-40 bg-yellow-300 rounded-full blur-2xl anim-float" />
          <div className="absolute bottom-10 right-10 w-56 h-56 bg-pink-300 rounded-full blur-2xl anim-float-slow" />
          <div className="absolute top-1/2 left-1/3 w-28 h-28 bg-white rounded-full blur-2xl" />
        </div>

        {/* Floating stars */}
        <div className="absolute top-12 right-1/4 anim-float-slow"><Star className="w-10 h-10 text-yellow-200 fill-yellow-200" /></div>
        <div className="absolute bottom-20 left-1/4 anim-float"><Sparkles className="w-9 h-9 text-yellow-100 fill-yellow-100" /></div>

        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <div className="inline-block anim-wiggle mb-4">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center sticker-shadow mx-auto">
              <Rocket className="w-10 h-10 text-emerald-600" />
            </div>
          </div>
          <h2 className="font-fun text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Ready to help your <br/>child <span className="text-yellow-300">shine bright?</span>
          </h2>
          <p className="text-xl text-emerald-50 mb-10 max-w-2xl mx-auto font-medium">
            Join Growing Minds today — high-fives, gold stars and big smiles included.
          </p>
          <Link to="/register">
            <Button className="bg-yellow-300 hover:bg-yellow-400 text-emerald-900 text-xl py-7 px-10 font-extrabold rounded-full shadow-2xl hover:-translate-y-1 transition-all" data-testid="footer-cta">
              Get Started — It&apos;s Free <ArrowRight className="ml-2 w-6 h-6" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-emerald-500 rounded-2xl flex items-center justify-center text-white font-extrabold">GM</div>
              <span className="text-white font-extrabold font-fun text-lg">Growing Minds Tuition</span>
            </div>
            <p className="text-slate-400 text-sm font-semibold">info@growingmindstuitionservices.co.uk</p>
            <p className="text-slate-500 text-sm">&copy; {new Date().getFullYear()} Growing Minds Tuition. UK National Curriculum Aligned.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
